import { z } from 'zod';
import type { Cents } from './money.js';
import { type VehicleClass, vehicleClasses } from './vehicle-class.js';

const priceCents = z.number().int().positive();

/**
 * Serviço extra do catálogo do lote (#451): lavagem, cera e afins, vendidos
 * no mesmo ticket do estacionamento.
 *
 * A placa da Belém Park é o modelo: `LAVAGEM SIMPLES carros pequenos R$ 25,00
 * / grandes e importados R$ 30,00`. Vira um preço base de R$ 25,00 e o preço
 * de R$ 30,00 só para `grande`.
 */
export const lotServiceInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** Preço de qualquer classe sem preço próprio. Serviço de graça não é serviço. */
  amountCents: priceCents,
  /** Preço por classe, quando a placa separa. Classe ausente paga o base. */
  classPrices: z
    .object(
      Object.fromEntries(vehicleClasses.map((c) => [c, priceCents.optional()])) as Record<
        VehicleClass,
        z.ZodOptional<typeof priceCents>
      >,
    )
    .strict()
    .nullish(),
});

export type LotServiceInput = z.infer<typeof lotServiceInputSchema>;

export type ServiceClassPrices = Partial<Record<VehicleClass, Cents>>;

/** Preço do serviço para a classe do veículo do ticket. */
export const servicePriceFor = (
  service: { amountCents: Cents; classPrices?: ServiceClassPrices | null },
  vehicleClass: VehicleClass,
): Cents => service.classPrices?.[vehicleClass] ?? service.amountCents;

/**
 * Serviço lançado num ticket, do jeito que o motor de preço recebe: nome e
 * preço já congelados no momento em que o operador lançou.
 */
export interface TicketServiceCharge {
  name: string;
  amountCents: Cents;
}

/**
 * Por que os serviços de um ticket não mudam mais, ou `null` enquanto podem
 * mudar (#451). Serviço só entra ou sai enquanto ninguém cotou o valor para
 * pagamento: depois da trava, do pagamento ou do chamado do valet, o cliente
 * já viu (ou pagou) um total, e mudar a conta por baixo dele faria o
 * fechamento registrar outro número. A API recusa com este texto, e o app
 * mostra o mesmo.
 */
export const servicesFrozenReason = (ticket: {
  status: string;
  mode: string;
  paidAt: unknown;
  settlementLockedAt: unknown;
  requestedAt: unknown;
}): string | null => {
  if (ticket.status !== 'open') return 'Ticket não está aberto.';
  if (ticket.paidAt) return 'Este ticket já foi pago, e o valor não muda mais.';
  if (ticket.settlementLockedAt) return 'O valor deste ticket já está travado.';
  if (ticket.mode === 'valet' && ticket.requestedAt) {
    return 'O cliente já chamou o carro, e o valor não muda mais.';
  }
  return null;
};
